// Avisos e caixa de entrada da Visão Geral — #6494, projeto 24.
//
// Avisos: os alertas que a Vick já grava em `ia_alertas` (vigia de hora em hora)
// aparecem na tela de quem resolve cada um; o WhatsApp fora do ar só para a
// Tecnologia (decisão de 27/10/2026: nada técnico para a Diretoria). Quem lê
// cada alerta continua decidido pela RLS (`ia_alerta_visivel`: adm ou permissão
// `vick:<modulo>`); aqui só se escolhe o que cada tela mostra.
//
// Caixa de entrada: conversas do Inbox da conexão da pessoa (`atendente_id`)
// cuja última mensagem veio do cliente.
//
// Lógica pura.

import type { PerfilVisaoGeral } from './perfis.ts';

export const ALERTAS_POR_PERFIL: Record<PerfilVisaoGeral, readonly string[]> = {
  'diretoria': ['usina_parada', 'inadimplencia_acumulando', 'queda_recebimento'],
  'gestao-financeiro': ['boleto_nao_emitido', 'boleto_sem_codigo_barras', 'boleto_orfao', 'inadimplencia_acumulando', 'queda_recebimento'],
  'faturamento': ['boleto_nao_emitido', 'boleto_sem_codigo_barras', 'boleto_orfao'],
  'backoffice': ['usina_parada', 'saldo_baixo', 'saldo_excedente', 'reincluir_no_rateio', 'variacao_consumo'],
  'cobranca': ['inadimplencia_acumulando', 'queda_recebimento'],
  'comercial': [],
  'vendas': [],
  'parceiros': [],
  'pos-venda': [],
  'tecnologia-marketing': [],
  'tecnologia': [],
  'marketing': [],
  'campo': [],
  'padrao': [],
};

/** Quem vê o aviso de WhatsApp fora do ar (conexões do Inbox com atendente). */
export const veQuedaWhatsApp = (perfil: PerfilVisaoGeral) => perfil === 'tecnologia' || perfil === 'tecnologia-marketing';

export const temAvisos = (perfil: PerfilVisaoGeral) => ALERTAS_POR_PERFIL[perfil].length > 0 || veQuedaWhatsApp(perfil);

export type Severidade = 'alta' | 'media' | 'baixa';

export interface Aviso {
  id: string;
  titulo: string;
  severidade: Severidade;
  link: string | null;
  /** Quando o problema começou (ISO). */
  desde: string | null;
  origem: 'vick' | 'whatsapp';
}

const PESO: Record<Severidade, number> = { alta: 0, media: 1, baixa: 2 };
const severidadeValida = (s: unknown): Severidade => (s === 'alta' || s === 'baixa' ? s : 'media');

export interface AlertaVick {
  id: number | string;
  titulo: string;
  severidade: string | null;
  link: string | null;
  detectado_em: string | null;
}

export const avisoDeAlerta = (a: AlertaVick): Aviso => ({
  id: `vick-${a.id}`,
  titulo: a.titulo,
  severidade: severidadeValida(a.severidade),
  link: a.link,
  desde: a.detectado_em,
  origem: 'vick',
});

/**
 * A Vick grava um alerta por UC nos tipos de boleto e de usina. Sem agrupar, cinco
 * "Boleto sem código de barras" ocupam o bloco inteiro e escondem o resto.
 * Plural usado quando o tipo tem mais de um alerta aberto.
 */
const PLURAL_TIPO: Record<string, string> = {
  boleto_nao_emitido: 'faturas sem cobrança vencendo em breve',
  boleto_orfao: 'boletos anteriores que podem estar em aberto',
  boleto_sem_codigo_barras: 'boletos sem código de barras',
  usina_parada: 'geradoras paradas ou sem telemetria',
};

/** Um aviso por tipo: o próprio alerta quando há um só; senão "N <plural>", com a maior gravidade e o início mais antigo. */
export function agruparAlertas(alertas: (AlertaVick & { tipo: string })[]): Aviso[] {
  const porTipo = new Map<string, (AlertaVick & { tipo: string })[]>();
  for (const a of alertas) {
    if (!porTipo.has(a.tipo)) porTipo.set(a.tipo, []);
    porTipo.get(a.tipo)!.push(a);
  }
  const avisos: Aviso[] = [];
  for (const [tipo, grupo] of porTipo) {
    if (grupo.length === 1) { avisos.push(avisoDeAlerta(grupo[0])); continue; }
    const severidade = grupo.map(g => severidadeValida(g.severidade)).sort((x, y) => PESO[x] - PESO[y])[0];
    const desde = grupo.map(g => g.detectado_em).filter(Boolean).sort()[0] ?? null;
    avisos.push({
      id: `vick-${tipo}`,
      titulo: `${grupo.length} ${PLURAL_TIPO[tipo] ?? `alertas do tipo ${tipo.replace(/_/g, ' ')}`}`,
      severidade,
      link: grupo[0].link,
      desde,
      origem: 'vick',
    });
  }
  return avisos;
}

export interface ConexaoInbox {
  id: string;
  display_name: string | null;
  status: string | null;
  caiu_em: string | null;
  atendente_id: string | null;
}

/** Conexão de atendente que não está ativa (o monitor do Inbox só olha as que têm atendente). */
export const conexaoFora = (c: ConexaoInbox) => !!c.atendente_id && (c.status !== 'active' || !!c.caiu_em);

export function avisosDeConexoes(conexoes: ConexaoInbox[]): Aviso[] {
  return conexoes.filter(conexaoFora).map(c => ({
    id: `whatsapp-${c.id}`,
    titulo: `WhatsApp fora do ar: ${c.display_name || 'conexão sem nome'}`,
    severidade: 'alta' as const,
    link: '/inbox/configuracoes',
    desde: c.caiu_em,
    origem: 'whatsapp' as const,
  }));
}

/** Mais grave primeiro; na mesma gravidade, o mais antigo (está esperando há mais tempo). */
export function ordenarAvisos(avisos: Aviso[], max = 5): Aviso[] {
  return [...avisos]
    .sort((a, b) => PESO[a.severidade] - PESO[b.severidade] || String(a.desde || '9').localeCompare(String(b.desde || '9')))
    .slice(0, max);
}

// ── Caixa de entrada ──

export interface ConversaEsperando {
  id: string;
  contato_nome: string | null;
  ultima_mensagem_em: string | null;
  negociacao_id: number | null;
}

export interface ResumoCaixa {
  /** Esperando resposta há até 24h: o que pede resposta agora. Mais antiga primeiro. */
  recentes: ConversaEsperando[];
  /** Paradas há mais de 24h com a última mensagem do cliente: em geral conversa terminada e não fechada. */
  antigas: number;
}

const DIA_MS = 24 * 60 * 60 * 1000;

export function resumirCaixa(conversas: ConversaEsperando[], agora: Date): ResumoCaixa {
  const limite = agora.getTime() - DIA_MS;
  const recentes: ConversaEsperando[] = [];
  let antigas = 0;
  for (const c of conversas) {
    const em = c.ultima_mensagem_em ? Date.parse(c.ultima_mensagem_em) : NaN;
    if (Number.isFinite(em) && em >= limite) recentes.push(c);
    else antigas++;
  }
  recentes.sort((a, b) => String(a.ultima_mensagem_em).localeCompare(String(b.ultima_mensagem_em)));
  return { recentes, antigas };
}

/** "agora", "12 min", "3 h", "2 dias". */
export function tempoDesde(iso: string | null, agora: Date): string {
  if (!iso) return '';
  const min = Math.max(0, Math.floor((agora.getTime() - Date.parse(iso)) / 60000));
  if (!Number.isFinite(min)) return '';
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} ${d === 1 ? 'dia' : 'dias'}`;
}
